import { onApiError } from "../error"
import { objectToQueryString } from "../utils"
import { del, get, put } from "./client"

/**
 * @param {string} imei 
 * @returns {Promise<Smartplug>}
 */
async function delSmartplugSubscription(imei) {
    const res = await del(`/smartplugs/${imei}/subscription`)
    if (!res.ok) throw await onApiError(res)
    return res.json()
}

/**
 * @param {string} imei
 * @returns {Promise<Smartplug>}
 */
async function getSmartplug(imei) {
    const res = await get(`/smartplugs/${imei}`)

    if (res.status === 408) throw new Error('TIMEOUT')
    if (!res.ok) throw await onApiError(res)

    const plug = await res.json()
    return plug
}

/**
 * @typedef {Object} ConsumptionDataPoint
 * @property {number} Consumption
 * @property {string} Imei
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<ConsumptionDataPoint[]|Blob>}
 */
async function getSmartplugConsumption(imei, args) {
    const params = objectToQueryString(args)
    const res = await get(`/smartplugs/${imei}/consumption${params}`)
    if (!res.ok) throw await onApiError(res)
    if (!args || args.format !== "json") return res.blob()
    return res.json()
}

/**
 * @typedef {Object} LoadDataPoint
 * @property {string} Imei
 * @property {number} Load
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<LoadDataPoint[]|Blob>}
 */
async function getSmartplugLoad(imei, args) {
    const params = objectToQueryString(args)
    const res = await get(`/smartplugs/${imei}/load${params}`)
    if (!res.ok) throw await onApiError(res)
    if (!args || args.format !== "json") return res.blob()
    return res.json()
}

/**
 * @typedef {Object} TemperatureDataPoint
 * @property {string} Imei
 * @property {number} Temperature
 * @property {string} Timestamp
 */

/**
 * @param {string} imei
 * @param {DataPointQueryParams} [args]
 * @returns {Promise<TemperatureDataPoint[]|Blob>}
 */
async function getSmartplugTemperature(imei, args) {
    const params = objectToQueryString(args)
    const res = await get(`/smartplugs/${imei}/temperature${params}`)
    if (!res.ok) throw await onApiError(res)
    if (!args || args.format !== "json") return res.blob()
    return res.json()
}

/**
 * @param {string} imei
 * @param {PutSmartplugParams} args
 * @returns {Promise<Smartplug>}
 */
async function putSmartplug(imei, args) {
    const res = await put(`/smartplugs/${imei}`, {
        body: JSON.stringify(args)
    })

    if (!res.ok) throw await onApiError(res)

    const plug = await res.json()
    return plug
}

/**
 * @param {string} imei 
 * @param {string} email 
 * @returns {Promise<Smartplug>}
 */
async function putSmartplugOwner(imei, email) {
    const res = await put(`/smartplugs/${imei}/owner`, {
        body: JSON.stringify({
            Email: email,
        })
    })
    if (!res.ok) throw await onApiError(res)
    return res.json()
}

/**
 * @param {string} imei
 * @param {boolean} enabled
 * @returns {Promise<Smartplug>}
 */
async function putSmartplugPowerEnabled(imei, enabled) {
    const res = await put(`/smartplugs/${imei}/power-enabled`, {
        body: JSON.stringify({
            Enabled: enabled
        }),
        timeout: 1000 * 60,
    })

    if (!res.ok) throw await onApiError(res)
    return res.json()
}

/**
 * @param {string} imei
 * @param {number} days 
 * @returns {Promise<Smartplug>}
 */
async function putSmartplugTrial(imei, days) {
    const res = await put(`/smartplugs/${imei}/trial`, {
        body: JSON.stringify({
            Days: days
        })
    })

    if (!res.ok) throw await onApiError(res)
    return res.json()
}

/**
 * @param {string} imei
 * @returns {Promise<Smartplug>}
 */
async function resetSmartplugConsumption(imei) {
  const res = await put(`/smartplugs/${imei}/consumption/reset`)
  if (!res.ok) throw await onApiError(res)
  return res.json()
}

/**
 * @param {string} imei
 */
async function unregisterSmartplug(imei) {
    const res = await del(`/smartplugs/${imei}`)
    if (!res.ok) throw await onApiError(res)
}

export {
    delSmartplugSubscription,
    getSmartplug,
    getSmartplugConsumption,
    getSmartplugLoad,
    getSmartplugTemperature,
    putSmartplug,
    putSmartplugOwner,
    putSmartplugPowerEnabled,
    putSmartplugTrial,
    resetSmartplugConsumption,
    unregisterSmartplug,
}